import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { apiClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { AppShellSkeleton } from '~/components/common/app-shell-skeleton/app-shell-skeleton';
import { LoadingSpinner } from '~/components/common/loading-spinner/loading-spinner';
import { fetchCurrentUser } from '~/features/auth/services/auth.service';
import { getOnboardingStatus } from '~/features/onboarding/services/onboarding-status.service';
import type { TenantDetail } from '~/hooks/useTenants';
import { useAuthStore } from '~/store/auth.store';

interface AppBootstrapProps {
  children: ReactNode;
}

/**
 * Handles async app initialization inside React so the browser renders
 * a loading skeleton instead of a blank screen while auth + i18n resolve.
 *
 * Must wrap <App /> in main.tsx, outside Providers (uses Zustand + direct
 * queryClient calls — no React context needed).
 */
export function AppBootstrap({ children }: AppBootstrapProps) {
  const [ready, setReady] = useState(false);
  const hasToken = useAuthStore((s) => Boolean(s.accessToken));

  useEffect(() => {
    async function init() {
      const { setUser, setIsInitializing, accessToken } = useAuthStore.getState();
      setIsInitializing(true);

      if (accessToken) {
        try {
          const user = await queryClient.fetchQuery({
            queryKey: queryKeys.auth.me(),
            queryFn: fetchCurrentUser,
          });

          if (user) {
            // `/auth/me`'s currentSubsidiary is authoritative — the JWT claim
            // drives it server-side, so no client-side reconciliation runs.
            setUser(user); // also sets isInitializing: false
            // Prefetch tenant list into cache — useTenants() will use this on first render
            await queryClient.prefetchQuery({
              queryKey: queryKeys.subsidiaries.list(),
              queryFn: () => apiClient.get(endpoints.subsidiaries.list()).json<TenantDetail[]>(),
            });
            // Non-blocking prefetch — warms the cache for the index-route
            // beforeLoad which gates `/` between `/onboarding`,
            // `/onboarding/complete-import` and `/experiments`.
            void queryClient.prefetchQuery({
              queryKey: queryKeys.onboarding.status(),
              queryFn: getOnboardingStatus,
            });
          } else {
            setIsInitializing(false);
          }
        } catch {
          // Network error — user stays null, not authenticated
          setIsInitializing(false);
        }
      } else {
        setIsInitializing(false);
      }

      setReady(true);
    }

    void init();
  }, []);

  if (!ready) return hasToken ? <AppShellSkeleton /> : <LoadingSpinner />;
  return <>{children}</>;
}
